import { useState, useEffect } from 'react'
import { activityApi, campaignApi } from '../api'
import type { AuditLog, Campaign } from '../types'

export function ActivityFeed() {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Filters
  const [selectedCampaign, setSelectedCampaign] = useState('')
  const [selectedActionType, setSelectedActionType] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  useEffect(() => {
    loadCampaigns()
    loadLogs()
  }, [])

  const loadCampaigns = async () => {
    try {
      const data = await campaignApi.list()
      setCampaigns(data.campaigns)
    } catch (err) {
      console.error('Failed to load campaigns:', err)
    }
  }

  const loadLogs = async () => {
    setLoading(true)
    try {
      const filters: any = {}
      if (selectedCampaign) filters.campaignId = selectedCampaign
      if (selectedActionType) filters.actionType = selectedActionType
      if (startDate) filters.startDate = startDate
      if (endDate) filters.endDate = endDate

      const data = await activityApi.list(filters)
      setLogs(data.logs)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity logs')
    } finally {
      setLoading(false)
    }
  }

  const handleApplyFilters = () => {
    loadLogs()
  }

  const handleClearFilters = () => {
    setSelectedCampaign('')
    setSelectedActionType('')
    setStartDate('')
    setEndDate('')
    setTimeout(() => loadLogs(), 0)
  }

  const actionTypes = [
    'post_detected',
    'comment_generated',
    'comment_approved',
    'comment_rejected',
    'comment_posted',
    'comment_failed',
    'campaign_activated',
    'campaign_deactivated',
  ]

  const getActionColor = (actionType: string) => {
    if (actionType.includes('failed') || actionType.includes('rejected')) return '#dc3545'
    if (actionType.includes('approved') || actionType.includes('posted')) return '#28a745'
    if (actionType.includes('activated')) return '#007bff'
    return '#6c757d'
  }

  const getCampaignName = (campaignId?: string) => {
    if (!campaignId) return 'N/A'
    const campaign = campaigns.find((c) => c.id === campaignId)
    return campaign ? campaign.name : campaignId
  }

  return (
    <div>
      <h2>Activity Feed</h2>

      {/* Filters */}
      <div
        style={{
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        <h3 style={{ margin: '0 0 15px 0', fontSize: '18px' }}>Filters</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              Campaign
            </label>
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            >
              <option value="">All Campaigns</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              Action Type
            </label>
            <select
              value={selectedActionType}
              onChange={(e) => setSelectedActionType(e.target.value)}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            >
              <option value="">All Actions</option>
              {actionTypes.map((type) => (
                <option key={type} value={type}>
                  {type.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            />
          </div>
        </div>
        <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
          <button
            onClick={handleApplyFilters}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Activity Logs */}
      {loading ? (
        <div>Loading activity logs...</div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : logs.length === 0 ? (
        <p>No activity logs found.</p>
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {logs.map((log) => (
            <div
              key={log.id}
              style={{
                padding: '15px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                    <span
                      style={{
                        padding: '2px 8px',
                        backgroundColor: getActionColor(log.actionType),
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      {log.actionType.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <span style={{ color: '#666', fontSize: '14px' }}>
                      {new Date(log.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {log.campaignId && (
                    <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                      Campaign: {getCampaignName(log.campaignId)}
                    </p>
                  )}
                  <p style={{ margin: '5px 0', fontSize: '14px' }}>{log.details}</p>
                  <p
                    style={{
                      margin: '5px 0',
                      fontSize: '14px',
                      color: log.outcome === 'success' ? '#28a745' : '#dc3545',
                    }}
                  >
                    Outcome: {log.outcome}
                  </p>
                  {log.error && (
                    <p style={{ margin: '5px 0', fontSize: '14px', color: '#dc3545' }}>
                      Error: {log.error}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
